// @flow

import memoize from 'memoize-one';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { withPageMeta } from '@gandi/app-runner';
import { needsAuth } from '@gandi/authentication';
import {
  selectCurrency as selectPrepaidCurrency,
  createPrepaid,
  replacePrepaid,
} from '@gandi/billing';
import { setCartProductOwner } from '@gandi/cart';
import {
  fetchContracts,
  selectContracts,
  computeLabel,
  contractsResource,
} from '@gandi/contracts';
import { notifyDanger } from '@gandi/notifications';
import {
  patchOneOrganization as patchOrg,
  createOneOrganization as createOrg,
  organizationsResource,
} from '@gandi/redux-organization';
import { selectBuId } from '@gandi/redux-places';
import {
  fetchOneOrganization,
  setCurrentId,
  selectPersonalId,
} from '@gandi/redux-sharing';
import { withTranslator } from '@gandi/translate';

import { getFormValues, actions } from 'gandi.react-form';
import { withLogger } from 'gandi.redux-logger';

import { withRouter } from 'src/helpers/withRouter';

import { selectShopCurrent } from '../../modules/organization/selectors';
import type { FullState, MapDispatchToProps } from '../../types';

import Presenter, {
  type StateProps,
  type DispatchProps,
  type Props,
} from './Presenter';

const contractId = 'reseller';
const FORM_NAME = 'accountForm';

const getContractParams = memoize((buId: ?number, sharingId: ?string) => ({
  contractId,
  buId,
  sharingId,
}));

const selectOrgFormValues = getFormValues(FORM_NAME);
const mapStateToProps = (state: FullState, props: Props): StateProps => {
  const update = props.params.id !== 'new';
  const org = selectShopCurrent(state, true);
  const personalId = selectPersonalId(state);
  const orgFormValues = selectOrgFormValues(state);
  const buId = selectBuId(state, orgFormValues?.country);

  const currency = update
    ? // $FlowFixMe[incompatible-call]
      selectPrepaidCurrency(state, { sharingId: org.id })
    : undefined;

  const contractParams = getContractParams(buId, update ? org.id : undefined);
  // $FlowFixMe[incompatible-call]
  const contractLabel = computeLabel(contractParams);

  const contracts = selectContracts(state, contractLabel);
  const loadedContracts = contractsResource.selectIsLoaded(
    state,
    contractLabel,
  );
  const fetchingContracts = contractsResource.selectIsFetching(
    state,
    contractLabel,
  );
  const failureContracts = contractsResource.selectIsFetching(
    state,
    contractLabel,
  );

  return {
    org,
    personalId,
    orgFormValues,
    currency,
    contracts,
    // $FlowFixMe[incompatible-return]: XXX `getContractParams` should cast buId as string in order to be used with `@gandi/contracts`
    contractParams,
    loadedContracts,
    fetchingContracts,
    failureContracts,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps> = {
  fetchContracts,
  createOrg,
  patchOrg,
  createPrepaid,
  replacePrepaid,
  setCurrentId,
  destroyForm: actions.destroy.bind(null, FORM_NAME),
  fetchOneOrganization,
  setCartProductOwner,
  invalidateOrganizations: organizationsResource.invalidate,
  notifyDanger,
};

export default (compose(
  withTranslator({ propsNamespace: 'translator' }),
  needsAuth,
  withRouter,
  withPageMeta(args => ({
    title: args.__('Choose billing and ownership information - Gandi.net'),
  })),
  withLogger('AccountChoiceConfirm'),
  connect(mapStateToProps, mapDispatchToProps),
)(Presenter): React.ComponentType<{}>);
